export const MINE_ARM_DELAY_MS = 1500;
export const MINE_LIFETIME_MS  = 30_000;
export const MINE_BLAST_RADIUS = 5;       // metres (standard)
export const MINE_WIDE_BLAST_RADIUS = 10; // metres (wide power-up)
export const MINE_DAMAGE       = 60;      // raw, before armour
export const MINE_DEPLOY_OFFSET = 3.5;    // metres behind vehicle
export const MINE_MAX_PER_PLAYER = 5;
export const MINE_RESTOCK_MS = 60_000;    // 1 mine restocks per minute

/** Structural subset used by pure helper functions — satisfied by MineState Schema. */
export interface MineLike {
  id: string;
  ownerId: string;
  x: number;
  z: number;
  deployedAt: number;
  wide: boolean;
}

/** Position at which a mine should be placed (3.5 m behind the vehicle). */
export function mineDeployPosition(
  playerX: number,
  playerZ: number,
  rotY: number,
): { x: number; z: number } {
  return {
    x: playerX - Math.sin(rotY) * MINE_DEPLOY_OFFSET,
    z: playerZ - Math.cos(rotY) * MINE_DEPLOY_OFFSET,
  };
}

/** True once the arming delay has elapsed. */
export function isMineArmed(mine: MineLike, now: number): boolean {
  return now - mine.deployedAt >= MINE_ARM_DELAY_MS;
}

/** True when the mine has exceeded its maximum lifetime. */
export function isMineExpired(mine: MineLike, now: number): boolean {
  return now - mine.deployedAt >= MINE_LIFETIME_MS;
}

/**
 * True when `player` is within the blast radius AND the mine is authorised to
 * detonate against that player.
 *
 * Rules:
 * - Dead players never trigger a mine.
 * - The owner is immune while the mine is unarmed; after arming they are fair game.
 * - Any other alive player always triggers once the mine is armed.
 */
export function checkMineProximity(
  mine: MineLike,
  player: { id: string; x: number; z: number; alive: boolean },
  now: number,
): boolean {
  if (!player.alive) return false;
  const armed = isMineArmed(mine, now);
  if (!armed) return false; // nobody can trigger an unarmed mine
  const dx = player.x - mine.x;
  const dz = player.z - mine.z;
  const r = mine.wide ? MINE_WIDE_BLAST_RADIUS : MINE_BLAST_RADIUS;
  return dx * dx + dz * dz <= r * r;
}

/**
 * Raw damage a mine deals to a player, after armour reduction.
 * Mirrors the formula in CombatSystem.applyDamage.
 */
export function calcMineDamage(armour: number): number {
  const reduction = Math.max(0, 1 - armour / 20);
  return Math.max(1, MINE_DAMAGE * reduction);
}
